import { useEffect, useState, useCallback, useMemo, Fragment } from "react";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Card, CardContent } from "../components/ui/card";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Calendar as CalendarIcon,
  FileDown,
  Search,
  ClipboardList,
  ArrowUpFromLine,
  ArrowDownToLine,
  Hourglass,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "../lib/utils";
import { useToast } from "../components/ui/use-toast";
import {
  get_indent_report,
  get_report_filter_data,
  server_post_data,
} from "../ServiceConnection/serviceconnection";
import XLSX from "sheetjs-style";
import { handleError } from "../CommonJquery/CommonJquery";

// Backend status_map = {0: 'Pending', 1: 'Partial', 2: 'Completed'}
// The value sent to the API must be the numeric code (as a string),
// the label is only for display.
const INDENT_STATUSES = [
  { value: "0", label: "Pending" },
  { value: "1", label: "Partial" },
  { value: "2", label: "Completed" },
];

const statusBadgeClass = (statusLabel) => {
  switch ((statusLabel || "").toUpperCase()) {
    case "PENDING":
      return "bg-warning/10 text-warning";
    case "PARTIAL":
      return "bg-warning/10 text-warning";
    case "COMPLETED":
      return "bg-success/10 text-success";
    default:
      return "bg-muted text-muted-foreground";
  }
};

export default function IndentReport() {
  const { toast } = useToast();

  // Filter states
  const [filterOption, setFilterOption] = useState("");
  const [startCal, setStartCal] = useState(new Date());
  const [endCal, setEndCal] = useState(new Date());
  const [departmentId, setDepartmentId] = useState("all");
  const [requestStatus, setRequestStatus] = useState("all");
  const [pendingView, setPendingView] = useState("all"); // all | pending (client-side only, backend has no such filter)

  // Filter dropdown data
  const [departments, setDepartments] = useState([]);

  // Data states
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [indentData, setIndentData] = useState([]);
  const [statusBreakdown, setStatusBreakdown] = useState({});
  const [expandedIndentId, setExpandedIndentId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const entriesPerPage = 10;

  // Stats (from API summary - reflects the date/department/status filters,
  // NOT the client-side "pending only" view toggle)
  const [stats, setStats] = useState({
    totalIndents: 0,
    totalRequested: 0,
    totalIssued: 0,
    totalPending: 0,
  });

  // Format number
  const formatNumber = (num) => {
    if (num === "-") return "-";
    if (num === undefined || num === null) return 0;
    const parsed = parseFloat(num);
    if (isNaN(parsed)) return 0;
    return parsed % 1 === 0 ? parsed : parseFloat(parsed.toFixed(2));
  };

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Fetch dropdown filter data (departments)
  const fetchFilterData = useCallback(async () => {
    try {
      const fd = new FormData();
      const response = await server_post_data(get_report_filter_data, fd);
      if (!response.data.error) {
        setDepartments(response.data.message.departments || []);
      }
    } catch (error) {
      console.error("Error fetching filter data:", error);
    }
  }, []);

  // Fetch indent data
  const fetchIndentData = useCallback(
    async (start_date, end_date, flag = "1") => {
      if (flag === "2") {
        if (filterOption === "today") {
          const today = new Date();
          start_date = new Date(today.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_week") {
          const today = new Date();
          const firstDayOfWeek = new Date(
            today.setDate(today.getDate() - today.getDay()),
          );
          start_date = new Date(firstDayOfWeek.setHours(0, 0, 0, 0));
          end_date = new Date(today.setHours(23, 59, 59, 999));
        } else if (filterOption === "this_month") {
          const today = new Date();
          start_date = new Date(
            today.getFullYear(),
            today.getMonth(),
            1,
            0,
            0,
            0,
          );
          end_date = new Date(
            today.getFullYear(),
            today.getMonth() + 1,
            0,
            23,
            59,
            59,
          );
        } else if (filterOption === "this_year") {
          const today = new Date();
          start_date = new Date(today.getFullYear(), 0, 1, 0, 0, 0);
          end_date = new Date(today.getFullYear(), 11, 31, 23, 59, 59);
        } else if (filterOption === "custom") {
          if (!startCal || !endCal) {
            toast({
              title: "Validation Error",
              description: "Please select both start and end dates.",
              variant: "destructive",
            });
            return;
          }
          start_date = startCal;
          end_date = endCal;
        }

        if (start_date > end_date) {
          toast({
            title: "Validation Error",
            description: "Start date cannot be greater than end date.",
            variant: "destructive",
          });
          return;
        }
      }

      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("start_date", formatDateLocal(start_date));
      fd.append("end_date", formatDateLocal(end_date));
      if (departmentId !== "all") fd.append("department_id", departmentId);
      // Backend reads request.POST.get('request_status'), not 'status'
      if (requestStatus !== "all") fd.append("request_status", requestStatus);
      // Note: backend has no server-side "pending only" filter, so it is
      // applied client-side below via filteredIndentData. We intentionally
      // do NOT send a pending_only field since the backend ignores it.

      try {
        const response = await server_post_data(get_indent_report, fd);

        if (response.data.error) {
          toast({
            title: "Error",
            description: response.data.message,
            variant: "destructive",
          });
        } else {
          const summary = response.data.summary || {};

          // Header level qty alag unit ki thi (KG + ML) — ab items count.
          // Item level qty ingredient ke apne unit me hai, wo sahi hai.
          const data = (response.data.data || []).map((d) => {
            const total = d.total_items || 0;
            const full = d.items_fully_issued ?? 0;
            return {
              ...d,
              total_requested_qty: total,
              total_issued_qty: full,
              total_pending_qty: Math.max(total - full, 0),
              dispatch_stock_date: d.dispatch_date ?? d.dispatch_stock_date,
              department_name: d.issued_from
                ? `${d.issued_from} → ${d.department_name}`
                : d.department_name,
              items: (d.items || []).map((i) => ({
                ...i,
                requested_qty_display:
                  i.requested_qty ?? i.requested_qty_display,
                issued_qty_grams: i.issued_qty ?? i.issued_qty_grams,
                approved_qty: i.approved_qty ?? 0,
                fulfillment_percentage:
                  i.fulfilment_pct ?? i.fulfillment_percentage ?? 0,
              })),
            };
          });

          const rawBd = summary.status_breakdown || {};
          const breakdown = Array.isArray(rawBd)
            ? rawBd.reduce((acc, b) => {
                acc[b.status] = b.count;
                return acc;
              }, {})
            : rawBd;

          const sumReq = data.reduce((s, d) => s + d.total_requested_qty, 0);
          const sumIss = data.reduce((s, d) => s + d.total_issued_qty, 0);

          setIndentData(data);
          setStatusBreakdown(breakdown);
          setExpandedIndentId(null);
          setCurrentPage(1);
          setStats({
            totalIndents: summary.total_indents || 0,
            totalRequested: sumReq,
            totalIssued: sumIss,
            totalPending: Math.max(sumReq - sumIss, 0),
          });
        }
      } catch (error) {
        console.error("Error fetching indent data:", error);
        toast({
          title: "Error",
          description: "Failed to load indent data",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    [filterOption, startCal, endCal, departmentId, requestStatus, toast],
  );

  // Handle search
  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    fetchIndentData(startCal, endCal, "2");
  }, [startCal, endCal, fetchIndentData]);

  // Client-side "pending / partial only" view, since the backend endpoint
  // does not support filtering this server-side.
  const filteredIndentData = useMemo(() => {
    if (pendingView !== "pending") return indentData;
    return indentData.filter(
      (item) => item.request_status_label !== "Completed",
    );
  }, [indentData, pendingView]);

  // Excel export - 2 sheets (Indent summary + item level detail)
  function downloadExcel(orderData, filename = "Indent_Report.xlsx") {
    if (!orderData.length) {
      handleError("No data available for export!");
      return;
    }

    // Sheet 1: Indent level
    const indentSheetData = orderData.map((item, index) => ({
      "Sr. No.": index + 1,
      "Indent Number": item.indent_number || "-",
      "Request Date": item.request_date || "-",
      Department: item.department_name || "-",
      Status: item.request_status_label || "-",
      Priority: item.priority ?? "-",
      "Total Items": item.total_items,
      "Items Requested": item.total_requested_qty,
      "Items Fully Issued": item.total_issued_qty,
      "Items Pending": item.total_pending_qty,
      "Grand Amount": formatNumber(item.grand_amount),
      "Dispatch Date": item.dispatch_stock_date || "-",
      Remarks: item.remarks || "-",
    }));

    indentSheetData.push({
      "Sr. No.": "",
      "Indent Number": "",
      "Request Date": "",
      Department: "",
      Status: "TOTAL",
      Priority: "",
      "Total Items": "",
      "Items Requested": stats.totalRequested,
      "Items Fully Issued": stats.totalIssued,
      "Items Pending": stats.totalPending,
      "Grand Amount": "",
      "Dispatch Date": `Total Indents: ${stats.totalIndents}`,
      Remarks: "",
    });

    // Sheet 2: Item level
    const itemSheetData = [];
    orderData.forEach((indent) => {
      (indent.items || []).forEach((item) => {
        itemSheetData.push({
          "Indent Number": indent.indent_number || "-",
          Department: indent.department_name || "-",
          Status: indent.request_status_label || "-",
          Ingredient: item.ingredient_name || "-",
          "Requested Qty": formatNumber(item.requested_qty_display),
          "Approved Qty": formatNumber(item.approved_qty),
          "Issued Qty": formatNumber(item.issued_qty_grams),
          "Pending Qty": formatNumber(item.pending_qty),
          Unit: item.unit || "-",
          "Fulfillment %": formatNumber(item.fulfillment_percentage),
        });
      });
    });

    const indentSheet = XLSX.utils.json_to_sheet(indentSheetData);
    indentSheet["!cols"] = [
      { wch: 8 },
      { wch: 15 },
      { wch: 13 },
      { wch: 18 },
      { wch: 11 },
      { wch: 9 },
      { wch: 11 },
      { wch: 14 },
      { wch: 12 },
      { wch: 12 },
      { wch: 14 },
      { wch: 14 },
      { wch: 25 },
    ];

    const itemSheet = XLSX.utils.json_to_sheet(itemSheetData);
    itemSheet["!cols"] = [
      { wch: 15 },
      { wch: 18 },
      { wch: 11 },
      { wch: 22 },
      { wch: 14 },
      { wch: 13 },
      { wch: 12 },
      { wch: 12 },
      { wch: 8 },
      { wch: 13 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, indentSheet, "Indent Summary");
    XLSX.utils.book_append_sheet(workbook, itemSheet, "Item Detail");
    XLSX.writeFile(workbook, filename);
  }

  // Pagination (operates on the client-filtered list)
  const totalPages = Math.ceil(filteredIndentData.length / entriesPerPage);
  const currentData = filteredIndentData.slice(
    (currentPage - 1) * entriesPerPage,
    currentPage * entriesPerPage,
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber < 1 || pageNumber > totalPages) return;
    setCurrentPage(pageNumber);
    setExpandedIndentId(null);
  };

  const toggleExpand = (indentId) => {
    setExpandedIndentId(expandedIndentId === indentId ? null : indentId);
  };

  // Format date for table display (backend sends 'YYYY-MM-DD')
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString.replace(" ", "T")).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Initial load
  useEffect(() => {
    fetchFilterData();
    fetchIndentData(startCal, endCal, "1");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MainLayout>
      <Header
        title="Indent Report"
        subtitle="Kitchen and department item requests with fulfillment status"
      />

      <div className="p-6 space-y-6">
        {/* Stats Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {stats.totalIndents}
                </p>
                <p className="text-sm text-muted-foreground">Total Indents</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                <ArrowUpFromLine className="w-5 h-5 text-warning" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {formatNumber(stats.totalRequested)}
                </p>
                <p className="text-sm text-muted-foreground">Items Requested</p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <ArrowDownToLine className="w-5 h-5 text-success" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {formatNumber(stats.totalIssued)}
                </p>
                <p className="text-sm text-muted-foreground">
                  Items Fully Issued
                </p>
              </div>
            </div>
          </div>
          <div className="stat-card">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                <Hourglass className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">
                  {formatNumber(stats.totalPending)}
                </p>
                <p className="text-sm text-muted-foreground">Items Pending</p>
              </div>
            </div>
          </div>
        </div>

        {/* Status Breakdown Chips */}
        {Object.keys(statusBreakdown).length > 0 && (
          <div className="flex flex-wrap gap-2">
            {Object.entries(statusBreakdown).map(([st, count]) => (
              <span
                key={st}
                className={cn(
                  "inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium",
                  statusBadgeClass(st),
                )}
              >
                {st}: {count}
              </span>
            ))}
          </div>
        )}

        {/* Filters Card */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Date Filter Dropdown */}
              <div className="space-y-2">
                <Label>Filter</Label>
                <Select value={filterOption} onValueChange={setFilterOption}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Option" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="today">Today</SelectItem>
                    <SelectItem value="this_week">This Week</SelectItem>
                    <SelectItem value="this_month">This Month</SelectItem>
                    <SelectItem value="this_year">This Year</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>Start Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !startCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startCal ? (
                          format(startCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startCal}
                        onSelect={setStartCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {filterOption === "custom" && (
                <div className="space-y-2">
                  <Label>End Date</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !endCal && "text-muted-foreground",
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endCal ? (
                          format(endCal, "dd MMM yyyy")
                        ) : (
                          <span>Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endCal}
                        onSelect={setEndCal}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              )}

              {/* Department */}
              <div className="space-y-2">
                <Label>Department</Label>
                <Select value={departmentId} onValueChange={setDepartmentId}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Departments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Departments</SelectItem>
                    {departments.map((dept) => (
                      <SelectItem
                        key={dept.primary_id}
                        value={String(dept.primary_id)}
                      >
                        {dept.department_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={requestStatus} onValueChange={setRequestStatus}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {INDENT_STATUSES.map((st) => (
                      <SelectItem key={st.value} value={st.value}>
                        {st.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Pending View (client-side only) */}
              <div className="space-y-2">
                <Label>View</Label>
                <Select value={pendingView} onValueChange={setPendingView}>
                  <SelectTrigger>
                    <SelectValue placeholder="All Indents" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Indents</SelectItem>
                    <SelectItem value="pending">
                      Pending / Partial Only
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Action Buttons */}
              <div className="flex items-end gap-2">
                <Button
                  onClick={handleSearch}
                  className="gap-2"
                  disabled={showLoaderAdmin}
                >
                  <Search className="w-4 h-4" />
                  Search
                </Button>
                <Button
                  variant="outline"
                  onClick={() => downloadExcel(filteredIndentData)}
                  className="gap-2"
                  disabled={filteredIndentData.length === 0}
                >
                  <FileDown className="w-4 h-4" />
                  Export Excel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table Card */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead className="text-muted-foreground w-10"></TableHead>
                  <TableHead className="text-muted-foreground">Sr.No</TableHead>
                  <TableHead className="text-muted-foreground">
                    Indent No.
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Request Date
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Department
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Status
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Items
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Requested
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Fully Issued
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Pending Items
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    Priority
                  </TableHead>
                  <TableHead className="text-muted-foreground text-right">
                    Grand Amount
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {showLoaderAdmin ? (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      className="text-center text-muted-foreground"
                    >
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : currentData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={12}
                      className="text-center text-muted-foreground"
                    >
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((row, index) => (
                    <Fragment key={row.indent_id || index}>
                      <TableRow
                        className="border-border cursor-pointer"
                        onClick={() => toggleExpand(row.indent_id)}
                      >
                        <TableCell>
                          {expandedIndentId === row.indent_id ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </TableCell>
                        <TableCell>
                          {(currentPage - 1) * entriesPerPage + index + 1}
                        </TableCell>
                        <TableCell className="font-medium">
                          {row.indent_number || "-"}
                        </TableCell>
                        <TableCell className="whitespace-nowrap">
                          {formatDate(row.request_date)}
                        </TableCell>
                        <TableCell>{row.department_name || "-"}</TableCell>
                        <TableCell>
                          <span
                            className={cn(
                              "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium",
                              statusBadgeClass(row.request_status_label),
                            )}
                          >
                            {row.request_status_label || "N/A"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {row.total_items}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.total_requested_qty)}
                        </TableCell>
                        <TableCell className="text-right text-success font-medium">
                          {formatNumber(row.total_issued_qty)}
                        </TableCell>
                        <TableCell
                          className={cn(
                            "text-right font-medium",
                            row.total_pending_qty > 0
                              ? "text-destructive"
                              : "text-muted-foreground",
                          )}
                        >
                          {formatNumber(row.total_pending_qty)}
                        </TableCell>
                        <TableCell>{row.priority ?? "-"}</TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.grand_amount)}
                        </TableCell>
                      </TableRow>

                      {/* Expanded item detail */}
                      {expandedIndentId === row.indent_id && (
                        <TableRow className="border-border bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={12} className="p-4">
                            {(row.remarks || row.dispatch_stock_date) && (
                              <div className="mb-3 text-xs text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
                                {row.dispatch_stock_date && (
                                  <span>
                                    Dispatch Date:{" "}
                                    {formatDate(row.dispatch_stock_date)}
                                  </span>
                                )}
                                {row.remarks && (
                                  <span>Remarks: {row.remarks}</span>
                                )}
                              </div>
                            )}
                            <div className="rounded-lg border border-border bg-card overflow-hidden">
                              <Table>
                                <TableHeader>
                                  <TableRow className="border-border hover:bg-transparent">
                                    <TableHead className="text-muted-foreground text-xs">
                                      Ingredient
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Requested
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Approved
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Issued
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs text-right">
                                      Pending
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs">
                                      Unit
                                    </TableHead>
                                    <TableHead className="text-muted-foreground text-xs">
                                      Fulfillment
                                    </TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {(row.items || []).map((item, i) => (
                                    <TableRow
                                      key={item.detail_id || i}
                                      className="border-border"
                                    >
                                      <TableCell className="font-medium text-sm">
                                        {item.ingredient_name || "-"}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {formatNumber(
                                          item.requested_qty_display,
                                        )}
                                      </TableCell>
                                      <TableCell className="text-right text-sm">
                                        {formatNumber(item.approved_qty)}
                                      </TableCell>
                                      <TableCell className="text-right text-sm text-success font-medium">
                                        {formatNumber(item.issued_qty_grams)}
                                      </TableCell>
                                      <TableCell
                                        className={cn(
                                          "text-right text-sm font-medium",
                                          item.pending_qty > 0
                                            ? "text-destructive"
                                            : "text-muted-foreground",
                                        )}
                                      >
                                        {formatNumber(item.pending_qty)}
                                      </TableCell>
                                      <TableCell className="text-sm">
                                        {item.unit || "-"}
                                      </TableCell>
                                      <TableCell>
                                        <div className="flex items-center gap-2 min-w-[120px]">
                                          <div className="h-2 flex-1 rounded-full bg-muted overflow-hidden">
                                            <div
                                              className={cn(
                                                "h-full rounded-full",
                                                item.fulfillment_percentage >=
                                                  100
                                                  ? "bg-success"
                                                  : item.fulfillment_percentage >
                                                      0
                                                    ? "bg-warning"
                                                    : "bg-muted-foreground/30",
                                              )}
                                              style={{
                                                width: `${Math.min(
                                                  item.fulfillment_percentage ||
                                                    0,
                                                  100,
                                                )}%`,
                                              }}
                                            />
                                          </div>
                                          <span className="text-xs text-muted-foreground w-10 text-right">
                                            {formatNumber(
                                              item.fulfillment_percentage,
                                            )}
                                            %
                                          </span>
                                        </div>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing {(currentPage - 1) * entriesPerPage + 1} to{" "}
                {Math.min(
                  currentPage * entriesPerPage,
                  filteredIndentData.length,
                )}{" "}
                of {filteredIndentData.length} entries
              </div>
              <div className="flex gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNumber = index + 1;
                  if (
                    pageNumber === 1 ||
                    pageNumber === totalPages ||
                    (pageNumber >= currentPage - 1 &&
                      pageNumber <= currentPage + 1)
                  ) {
                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pageNumber === currentPage ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => handlePageChange(pageNumber)}
                      >
                        {pageNumber}
                      </Button>
                    );
                  } else if (
                    pageNumber === currentPage - 2 ||
                    pageNumber === currentPage + 2
                  ) {
                    return <span key={pageNumber}>...</span>;
                  }
                  return null;
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
